import React, { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import axios from 'axios';
import BookService from './BookService';
import { Link } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';

import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import SalonReviewsDialog from './SalonReviewsDialog';

export default function OutlineCard(props) {
  const { salon, user } = props;
  // console.log('salon in Card:', salon);
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  const [services, setServices] = useState([]);
  const [bookingVisible, setBookingVisible] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({ apptDate: '', apptTime: '' });

  const [openReviews, setOpenReviews] = useState(false);

  const servicesUrl = process.env.REACT_APP_SERVER
    ? 'https://salonserver-k8m8.onrender.com/services'
    : 'http://localhost:3001/services';

  const filteredServices = (services) =>
    services.filter((service) => service.salon_id === salon.id);

  const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // India center
  const center = {
    lat: Number(salon.latitude) || defaultCenter.lat,
    lng: Number(salon.longitude) || defaultCenter.lng,
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.25 && rating - fullStars < 0.75;
    const totalStars = 5;

    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`full-${i}`} fontSize="small" color="warning" />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" fontSize="small" color="warning" />);
    }

    const remaining = totalStars - stars.length;
    for (let i = 0; i < remaining; i++) {
      stars.push(<StarBorderIcon key={`empty-${i}`} fontSize="small" color="warning" />);
    }

    return stars;
  };

  useEffect(() => {
    const getServicesData = async () => {
      try {
        const response = await axios.get(servicesUrl);
        setServices(response.data);
      } catch (err) {
        setError(err.message);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    getServicesData();
  }, []);

  return (
    <Card
      sx={{
        maxWidth: 600,
        margin: '0 auto',
        boxShadow: 3,
        borderRadius: 2,
        transition: 'transform 0.3s',
        '&:hover': { transform: 'scale(1.02)', boxShadow: 6 },
      }}
    >
      <Link to={{ pathname: `/salon/${salon.id}` }} key={salon.id}>
        <Tooltip title="Click on the image above to go to the Salon" arrow>
          <CardMedia
            component="img"
            height="200"
            image={salon.get_image_url}
            alt={salon.name}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
        </Tooltip>
      </Link>

      {bookingVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 2,
            boxShadow: 5,
          }}
        >
          <BookService
            booking={booking}
            setBooking={setBooking}
            salonId={salon.id}
            salonName={salon.name}
            salonEmail={salon.email}
            salonLandline={salon.landline}
            salonMobile={salon.mobile}
            services={filteredServices(services)}
            clientName={`${user.first_name} ${user.last_name}`}
            clientEmail={user.email}
            clientMobile={user.mobile}
            userId={user.id}
            setBookingVisible={setBookingVisible}
          />
          <Button
            variant="outlined"
            onClick={() => setBookingVisible(false)}
            sx={{ mt: 2, width: '100%' }}
          >
            Close
          </Button>
        </Box>
      )}

      <CardContent>

        <Typography
          variant="h5"
          component="div"
          gutterBottom
          sx={{ fontWeight: 'bold', color: 'primary.main' }}
        >
          {salon.name}
        </Typography>
        <Box
          onClick={() => setOpenReviews(true)}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <Typography variant="body2" color="text.secondary">
            {' '}
            {renderStars(salon.average_rating)}
            {' '}
            {salon.average_rating}
            {' '}
            ({salon.reviews_count} reviews)
            {/* {console.log('reviews', salon.reviews_count)} */}
          </Typography>
        </Box>
        <SalonReviewsDialog
          open={openReviews}
          onClose={() => setOpenReviews(false)}
          reviews={salon.reviews}
        />
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Address:</strong>
              <br />
              {salon.address1}
              <br />
              {salon.address2}
              <br />
              {salon.pincode}
              <br />
              {salon.city}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Services:</strong>
            </Typography>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {filteredServices(services).map((service) => (
                <li key={service.id}>
                  {service.stype} : {service.sprice}
                </li>
              ))}
            </ul>

          </Grid>

        </Grid>

        <Box sx={{ mt: 2 }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: 200 }}
            center={center}
            zoom={15}
            options={{ mapTypeId: 'roadmap', streetViewControl: false }}
          >
            <Marker position={{ lat: Number(salon.latitude), lng: Number(salon.longitude) }} />
          </GoogleMap>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'center', p: 2 }}>
        <Link to={{ pathname: `/salon/${salon.id}`, state: { user } }} key={salon.id}>
          <Button
            variant="contained"
            size="medium"
            sx={{ px: 3, py: 1, borderRadius: 1 }}
          >
            Visit Salon
          </Button>
        </Link>

      </CardActions>
    </Card>
  );
}